import * as Sentry from '@sentry/react-router';
import { getLocaleFromPathname } from 'dotcom-core/src/helpers/locale';
import env from '~/env';
import { getLocalizationCountryCookie, getShopStoreCodeCookie } from './cookie';
import { devConsole } from './monitoring';

declare global {
  interface Window {
    __BUILD_META__: { hash: string } | string;
  }
}

const NO_DATA = 'unknown';

function initSentryClient() {
  if (env.DEPLOYMENT_ENVIRONMENT === 'development') {
    devConsole.info('Sentry is disabled in development.');
    return;
  }

  Sentry.init({
    dsn: env?.APP_SENTRY_DSN,
    environment: env?.NODE_ENV,
    allowUrls: ['native-instruments.com'],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    beforeSend(event) {
      if (event.request?.url) {
        const url = new URL(event.request.url);
        if (
          url.protocol === 'chrome-extension:' ||
          url.protocol === 'moz-extension:'
        ) {
          // This error is from a browser extension, ignore it
          return null;
        }
      }
      return event;
    },
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      // Non-actionable network errors unhandled, mostly by third-parties.
      'AbortError',
      'Load failed',
      'Failed to fetch',
      "Failed to execute 'send' on 'XMLHttpRequest'",
      /Non-Error exception captured/,
      /Non-Error promise rejection captured/,
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });

  const appVersion =
    typeof window.__BUILD_META__ === 'object'
      ? window.__BUILD_META__.hash
      : NO_DATA;

  const scope = Sentry.getCurrentScope();
  scope.setTag('app_version', appVersion);
  scope.setTag('app_namespace', window.location.pathname.split('/')[2]);
  scope.setTag('app_language', getLocaleFromPathname(window.location.pathname));
  scope.setTag('app_country', getLocalizationCountryCookie() ?? NO_DATA);
  scope.setTag('app_store_code', getShopStoreCodeCookie() ?? NO_DATA);
  scope.setTag('error_type', 'genericError');
}

export { initSentryClient };
